import "components/DocumentStatusTabs/documentStatusTabs.css";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import PropTypes from "prop-types";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMaterialUIController } from "context";
import PickDate from "components/DatePicker/DatePicker";
import AppHeader from "components/AppHeader";
import { DashboardNavigation } from "layouts/businessUserDashboard";
import { ClearableSearchInput } from "components/SearchField/SearchField";
import ThemedSelect from "components/FormControls/ThemedSelect";
import { CardSkeleton, Skeleton } from "components/Skeleton";
import {
  LuSearch,
  LuCoins,
  LuTriangleAlert,
  LuX,
  LuChevronLeft,
  LuChevronRight,
  LuRefreshCw,
  LuCheck,
} from "react-icons/lu";
import { recoveryRequest } from "utils/recoveryAPI";
import { clearCachedRequest } from "utils/requestCache";
import "./recovery.css";
const date = (value) =>
  value && Number.isFinite(new Date(value).getTime())
    ? new Date(value).toLocaleDateString("en-AU", {
        day: "numeric",
        month: "short",
        year: "numeric",
        timeZone: "UTC",
      })
    : "Not recorded";
const longDate = (value) =>
  value && Number.isFinite(new Date(value).getTime())
    ? new Date(value).toLocaleString("en-AU", {
        day: "numeric",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        timeZoneName: "short",
      })
    : "Not recorded";
const inputDay = (value) =>
  value
    ? `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(
        2,
        "0"
      )}-${String(value.getDate()).padStart(2, "0")}`
    : "";
const localDate = (day) => (day ? new Date(`${day}T00:00:00`) : null);
const titles = { review: "Needs review", active: "Active", closed: "Closed" };
const RECOVERY_PAGE_SIZE = 10;
const eventTitles = {
  demand_sent: "Letter of demand sent",
  referral_submitted: "Referral approved and submitted",
  payment_recorded: "Payment recorded",
  paused: "Invoice follow-up paused",
  resumed: "Invoice follow-up resumed",
  disputed: "Dispute recorded · follow-up paused",
  partner_notified: "Partner notified of current balance",
  partner_notice_failed: "Partner update delivery failed",
};

function RecoveryLoadingSkeleton({ detail, demand }) {
  if (demand) {
    return (
      <div
        className="recovery-demand-grid recovery-loading-demand"
        role="status"
        aria-label="Loading debt collection"
      >
        <div>
          <article
            className="recovery-panel recovery-letter recovery-loading-letter"
            aria-label="Loading demand letter"
          >
            <Skeleton variant="text" width="28%" height={22} />
            <div className="recovery-letter-brand">
              <Skeleton variant="text" width="34%" height={18} />
              <Skeleton variant="pill" width={52} height={24} />
            </div>
            <div className="recovery-loading-copy">
              <Skeleton variant="text" width="42%" />
              <Skeleton variant="text" width="76%" />
              <Skeleton variant="text" width="96%" />
              <Skeleton variant="text" width="91%" />
              <Skeleton variant="text" width="68%" />
              <Skeleton variant="text" width="94%" />
            </div>
          </article>
          <Skeleton
            variant="text"
            width="62%"
            className="recovery-loading-attachment"
          />
        </div>
        <aside
          className="recovery-demand-aside"
          aria-label="Loading demand approval"
        >
          <section className="recovery-panel recovery-loading-confirm">
            <Skeleton variant="text" width="45%" height={22} />
            <Skeleton variant="text" width="30%" />
            <Skeleton variant="rounded" width="100%" height={40} />
            <Skeleton variant="text" width="65%" />
            <div className="recovery-loading-facts">
              <Skeleton variant="text" width="46%" />
              <Skeleton variant="text" width="38%" />
              <Skeleton variant="text" width="52%" />
              <Skeleton variant="text" width="44%" />
            </div>
            <Skeleton variant="rounded" width="100%" height={40} />
          </section>
          <div className="recovery-notice recovery-loading-notice">
            <Skeleton variant="circular" width={20} height={20} />
            <div>
              <Skeleton variant="text" width="42%" />
              <Skeleton variant="text" width="100%" />
              <Skeleton variant="text" width="82%" />
            </div>
          </div>
          <section className="recovery-panel recovery-loading-approval">
            <Skeleton variant="text" width="52%" height={22} />
            <Skeleton variant="text" width="100%" />
            <Skeleton variant="text" width="86%" />
            <Skeleton variant="rounded" width="100%" height={40} />
          </section>
        </aside>
      </div>
    );
  }

  return (
    <div
      className="recovery-loading-layout"
      role="status"
      aria-label="Loading debt collection"
    >
      <div className="recovery-context recovery-loading-context">
        <Skeleton variant="text" width={240} />
      </div>
      <section className="recovery-stats" aria-hidden="true">
        {[0, 1, 2].map((card) => (
          <article className="recovery-stat recovery-loading-stat" key={card}>
            <h2>
              <Skeleton variant="text" width="38%" />
              <Skeleton variant="circular" width={20} height={20} />
            </h2>
            <strong>
              <Skeleton variant="text" width="48%" height={28} />
            </strong>
            <p>
              <Skeleton variant="text" width="70%" />
            </p>
          </article>
        ))}
      </section>
      <nav className="recovery-tabs recovery-loading-tabs" aria-hidden="true">
        {[72, 60, 68].map((width, tab) => (
          <button type="button" disabled key={tab}>
            <Skeleton variant="text" width={width} />
          </button>
        ))}
      </nav>
      <div className={`recovery-workspace${detail ? " has-selection" : ""}`}>
        <div className="recovery-register">
          <div className="recovery-search recovery-loading-search">
            <Skeleton variant="circular" width={18} height={18} />
            <Skeleton variant="text" width={180} />
          </div>
          <section
            className="recovery-case-list recovery-loading-list"
            aria-label="Loading recovery register"
          >
            <header>
              <h2>
                <Skeleton variant="text" width={130} />
              </h2>
              <span>
                <Skeleton variant="text" width={34} />
              </span>
            </header>
            <div className="recovery-list-head">
              <strong>
                <Skeleton variant="text" width={118} />
              </strong>
              <strong>
                <Skeleton variant="text" width={52} />
              </strong>
            </div>
            {[0, 1, 2, 3].map((row) => (
              <div className="recovery-row recovery-loading-row" key={row}>
                <span>
                  <Skeleton variant="text" width="42%" />
                  <Skeleton variant="text" width="24%" />
                </span>
                <span className="recovery-row-balance">
                  <Skeleton variant="text" width={72} />
                </span>
                <span className="recovery-row-context">
                  <Skeleton variant="pill" width={78} height={24} />
                  <Skeleton variant="text" width={112} />
                </span>
                <small>
                  <Skeleton variant="text" width="58%" />
                </small>
              </div>
            ))}
            <footer>
              <Skeleton variant="text" width={110} />
              <div>
                <Skeleton variant="rounded" width={28} height={28} />
                <Skeleton variant="rounded" width={28} height={28} />
              </div>
            </footer>
          </section>
        </div>
        {detail && (
          <section
            className="recovery-case recovery-loading-case"
            aria-label="Loading recovery case"
          >
            <header>
              <Skeleton variant="text" width="46%" height={22} />
              <Skeleton variant="circular" width={32} height={32} />
            </header>
            <div className="recovery-case-body">
              <div className="recovery-case-status">
                <Skeleton variant="pill" width={84} height={24} />
                <Skeleton variant="text" width={76} />
              </div>
              <div className="recovery-loading-facts">
                {[0, 1, 2, 3, 4, 5].map((fact) => (
                  <Skeleton variant="text" width="80%" key={fact} />
                ))}
              </div>
              <Skeleton variant="rounded" width="100%" height={92} />
              <Skeleton variant="text" width="34%" height={20} />
              <Skeleton variant="text" width="92%" />
              <Skeleton variant="text" width="76%" />
            </div>
            <footer>
              <Skeleton variant="text" width={126} />
            </footer>
          </section>
        )}
      </div>
    </div>
  );
}

RecoveryLoadingSkeleton.propTypes = {
  detail: PropTypes.bool.isRequired,
  demand: PropTypes.bool.isRequired,
};

function historyFor(row) {
  const recorded = row.recovery?.history || [];
  const history = recorded.map((event) => ({
    ...event,
    title: eventTitles[event.action] || event.action,
  }));
  (row.reminders || []).forEach((event) =>
    history.push({
      at: event.reminderDateTime,
      title:
        event.isEmailSent === "Success"
          ? "Invoice reminder sent"
          : "Reminder delivery not confirmed",
      note:
        event.isEmailSent === "Success"
          ? "Email accepted for delivery to the invoice contact."
          : `Delivery status: ${event.isEmailSent}`,
    })
  );
  (row.invoicePayment || []).forEach((p) => {
    if (
      !recorded.some(
        (event) =>
          event.action === "payment_recorded" &&
          event.reference === p.paymentTransactionID &&
          Number(event.amount) === Number(p.paymentAmount)
      )
    )
      history.push({
        at: p.paymentDate,
        title: "Payment recorded",
        amount: p.paymentAmount,
        reference: p.paymentTransactionID,
      });
  });
  if (row.dueDate)
    history.push({
      at: new Date(new Date(row.dueDate).getTime() + 86400000).toISOString(),
      title: "Invoice became overdue",
      note: `Due ${date(row.dueDate)}.`,
    });
  if (row.recovery?.closedAt)
    history.push({
      at: row.recovery.closedAt,
      title: "Case closed · balance $0.00",
      note: "Paid in full. Automated reminders stopped.",
    });
  return history.sort((a, b) => new Date(b.at) - new Date(a.at));
}
export default function RecoveryDashboard() {
  const navigate = useNavigate(),
    { id, action } = useParams();
  const [controller] = useMaterialUIController();
  const [navOpen, setNavOpen] = useState(false),
    [data, setData] = useState(null),
    [loading, setLoading] = useState(true),
    [error, setError] = useState(""),
    [message, setMessage] = useState(""),
    [busy, setBusy] = useState(false);
  const [tab, setTab] = useState("review"),
    [search, setSearch] = useState(""),
    [currency, setCurrency] = useState("AUD"),
    [page, setPage] = useState(1),
    [selectionClosed, setSelectionClosed] = useState(false);
  const [responseDate, setResponseDate] = useState(""),
    [preview, setPreview] = useState(null),
    [previewLoading, setPreviewLoading] = useState(false),
    [approved, setApproved] = useState(false);
  const [payment, setPayment] = useState({
      amount: "",
      paymentDate: "",
      reference: "",
    }),
    [partner, setPartner] = useState({ name: "", email: "" });
  const dialogRef = useRef(null),
    caseCardRef = useRef(null),
    caseListRef = useRef(null);
  const user = useMemo(() => {
    try {
      return JSON.parse(sessionStorage.getItem("LOGIN_DETAILS") || "{}");
    } catch (_) {
      return {};
    }
  }, []);
  const load = useCallback(async () => {
    const result = await recoveryRequest("", undefined, "get");
    setData(result);
    return result;
  }, []);
  useEffect(() => {
    let mounted = true;
    load()
      .catch((e) => {
        if (mounted) setError(e.message);
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [load]);
  const currencies = useMemo(
    () => [
      ...new Set((data?.cases || []).map((row) => row.currencyValue || "AUD")),
    ],
    [data]
  );
  useEffect(() => {
    if (data && currencies.length && !currencies.includes(currency))
      setCurrency(currencies[0]);
  }, [data, currencies, currency]);
  useEffect(() => {
    if (id && data) {
      const target = data.cases.find((row) => String(row._id) === id);
      if (target) {
        setTab(target.group);
        setCurrency(target.currencyValue || "AUD");
      }
    }
  }, [id, data]);
  const cases = (data?.cases || []).filter(
    (row) => (row.currencyValue || "AUD") === currency
  );
  const counts = Object.fromEntries(
    Object.keys(titles).map((key) => [
      key,
      cases.filter((row) => row.group === key).length,
    ])
  );
  const filtered = cases.filter(
    (row) =>
      row.group === tab &&
      `${row.customerName || ""} ${row.invoiceNumber || ""}`
        .toLowerCase()
        .includes(search.trim().toLowerCase())
  );
  const pageCount = Math.max(
      1,
      Math.ceil(filtered.length / RECOVERY_PAGE_SIZE)
    ),
    currentPage = Math.max(1, Math.min(page, pageCount)),
    visible = filtered.slice(
      (currentPage - 1) * RECOVERY_PAGE_SIZE,
      currentPage * RECOVERY_PAGE_SIZE
    );
  const selected = id
    ? (data?.cases || []).find((row) => String(row._id) === id)
    : selectionClosed
    ? null
    : visible[0];
  useEffect(() => {
    const card = caseCardRef.current,
      list = caseListRef.current;
    if (!selected || !card || !list) return undefined;
    const syncHeight = () => {
      if (
        typeof window.matchMedia === "function" &&
        window.matchMedia("(max-width: 760px)").matches
      ) {
        list.style.removeProperty("height");
        list.style.removeProperty("max-height");
        return;
      }
      const cardBounds = card.getBoundingClientRect(),
        listBounds = list.getBoundingClientRect(),
        nextHeight = Math.round(cardBounds.bottom - listBounds.top);
      if (nextHeight > 0) {
        list.style.height = `${nextHeight}px`;
        list.style.maxHeight = `${nextHeight}px`;
      }
    };
    syncHeight();
    const frame = window.requestAnimationFrame(syncHeight);
    const observer =
      typeof ResizeObserver === "function"
        ? new ResizeObserver(syncHeight)
        : null;
    observer?.observe(card);
    window.addEventListener("resize", syncHeight);
    return () => {
      window.cancelAnimationFrame(frame);
      observer?.disconnect();
      window.removeEventListener("resize", syncHeight);
      list.style.removeProperty("height");
      list.style.removeProperty("max-height");
    };
  }, [selected]);
  const routeAction = action || "case";
  const money = (value, compact = false) =>
    new Intl.NumberFormat("en-AU", {
      style: "currency",
      currency: currency || "AUD",
      minimumFractionDigits: compact ? 0 : 2,
      maximumFractionDigits: compact ? 0 : 2,
    }).format(Number(value || 0));
  const amountOutstanding =
    cases.reduce((sum, row) => sum + Math.round(Number(row.balance) * 100), 0) /
    100;
  useEffect(() => setPage(1), [tab, search, currency]);
  useEffect(() => {
    setResponseDate("");
    setPreview(null);
    setApproved(false);
    setPayment({ amount: "", paymentDate: data?.today || "", reference: "" });
    setPartner({ name: "", email: "" });
  }, [id, action, data?.today]);
  useEffect(() => {
    let active = true;
    if (routeAction !== "demand" || !selected || !responseDate) {
      setPreview(null);
      return undefined;
    }
    setPreview(null);
    setApproved(false);
    setPreviewLoading(true);
    recoveryRequest(`/${selected._id}/demand/preview`, { responseDate })
      .then((result) => {
        if (active) setPreview(result);
      })
      .catch((e) => {
        if (active) setError(e.message);
      })
      .finally(() => {
        if (active) setPreviewLoading(false);
      });
    return () => {
      active = false;
    };
  }, [routeAction, selected, responseDate]);
  const closeAction = useCallback(() => {
    setApproved(false);
    setError("");
    if (id) navigate(`/debts/view/${id}`);
    else navigate("/debts");
  }, [id, navigate]);
  useEffect(() => {
    if (!["payment", "referral"].includes(routeAction)) return undefined;
    const prior = document.activeElement;
    dialogRef.current?.querySelector("input,button")?.focus();
    const keyboard = (e) => {
      if (e.key === "Escape" && !busy) closeAction();
      if (e.key === "Tab") {
        const elements = [
          ...dialogRef.current.querySelectorAll(
            "button:not(:disabled),input:not(:disabled),a[href]"
          ),
        ].filter((el) => el.getClientRects().length);
        if (!elements.length) return;
        const first = elements[0],
          last = elements[elements.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener("keydown", keyboard);
    return () => {
      document.removeEventListener("keydown", keyboard);
      prior?.focus?.();
    };
  }, [routeAction, busy, closeAction]);
  const run = async (path, body, returnToCase = false) => {
    if (busy || !selected) return;
    setBusy(true);
    setError("");
    setMessage("");
    try {
      const result = await recoveryRequest(`/${selected._id}/${path}`, {
        ...body,
        expectedRevision: selected.revision,
        expectedBalance: selected.balance,
      });
      setMessage(result.message);
      clearCachedRequest();
      const fresh = await load();
      const updated = fresh.cases.find((row) => row._id === selected._id);
      if (updated) setTab(updated.group);
      if (returnToCase) navigate(`/debts/view/${selected._id}`);
      return result;
    } catch (e) {
      setError(e.message);
      try {
        await load();
      } catch (_) {}
    } finally {
      setBusy(false);
    }
  };
  const select = (row) => {
    setSelectionClosed(false);
    setError("");
    setApproved(false);
    navigate(`/debts/view/${row._id}`);
  };
  const go = (next) => {
    setError("");
    setApproved(false);
    navigate(`/debts/view/${selected._id}/${next}`);
  };
  const canContact =
    selected &&
    data?.owner &&
    selected.canDemand !== false &&
    !selected.recovery?.disputed &&
    !selected.recovery?.paused &&
    selected.balance > 0;
  const referralReady =
    selected?.recovery?.demand?.status === "sent" &&
    selected.recovery.demand.responseDate < data?.today &&
    selected.recovery?.referral?.status !== "submitted";
  const partnerNoticeNeeded =
    selected?.recovery?.referral?.status === "submitted" &&
    (selected.recovery.partnerNoticeStatus === "failed" ||
      Number(selected.recovery.partnerNoticeBalance ?? -1) !==
        Number(selected.balance));
  const summary = selected && (
    <dl className="recovery-case-facts">
      <dt>Original invoice</dt>
      <dd>{money(selected.finalAmount)}</dd>
      <dt>Outstanding balance</dt>
      <dd>{money(selected.balance)}</dd>
      <dt>Due date</dt>
      <dd>{date(selected.dueDate)}</dd>
    </dl>
  );
  const responsible = (
    <div className="recovery-notice">
      <LuTriangleAlert />
      <div>
        <strong>Responsible follow-up</strong>
        <p>
          Pause disputed debts. Verify contacts and Australian demand terms,
          deadlines, fees and partner requirements. Follow privacy and lawful
          contact safeguards.
        </p>
      </div>
    </div>
  );
  const returnButton = (
    <button
      type="button"
      className="recovery-secondary"
      onClick={closeAction}
      disabled={busy}
    >
      Back to case
    </button>
  );
  const caseCard = selected && (
    <aside
      className="recovery-case"
      aria-label="Selected recovery case"
      ref={caseCardRef}
    >
      <header>
        <h2>Selected case</h2>
        <button
          type="button"
          aria-label="Close selected case"
          onClick={() => {
            setSelectionClosed(true);
            navigate("/debts");
          }}
        >
          <LuX />
        </button>
      </header>
      <div className="recovery-case-body">
        <div className="recovery-case-status">
          <Link to={`/invoices/view/${selected._id}`}>
            {selected.invoiceNumber}
          </Link>
          <span className={`recovery-badge is-${selected.group}`}>
            {selected.status}
          </span>
        </div>
        <h3>{selected.customerName || "Contact unavailable"}</h3>
        {summary}
        {selected.canDemand === false && selected.balance > 0 && <div className="recovery-notice"><p>Some instalments are not overdue. <Link to={`/invoices/view/${selected._id}`}>Review the invoice schedule</Link> before demanding the full balance.</p></div>}
        <div className="recovery-next">
          <strong>{selected.nextAction}</strong>
          <p>
            {selected.group === "closed"
              ? "Reminders stopped. No further collection action is available."
              : selected.recovery?.referral?.status === "submitted"
              ? "The referral was emailed to the receiving partner. Partner acceptance has not been confirmed."
              : selected.recovery?.demand?.status === "sent"
              ? `Demand sent ${date(
                  selected.recovery.demand.sentAt
                )}. Review any payment or dispute before taking the next action.`
              : "No automatic send. Check the response date and approve the wording."}
          </p>
          {selected.group === "review" && selected.balance > 0 && !selected.recovery?.demand?.sentAt && (
            <button
              type="button"
              className="recovery-primary"
              disabled={!canContact || busy}
              onClick={() => go("demand")}
            >
              Review demand
            </button>
          )}
          {referralReady && (
            <button
              type="button"
              className="recovery-primary"
              disabled={!canContact || busy}
              onClick={() => go("referral")}
            >
              Review collection referral
            </button>
          )}
        </div>
        {selected.balance > 0 && (
          <>
            <div className="recovery-automation">
              <div>
                <strong>Invoice automation</strong>
                <small>Off to pause · on to resume</small>
              </div>
              <label className="recovery-switch">
                <input
                  type="checkbox"
                  role="switch"
                  aria-label="Invoice automation"
                  checked={
                    !selected.recovery?.paused &&
                    !selected.recovery?.disputed &&
                    selected.recovery?.referral?.status !== "submitted"
                  }
                  disabled={
                    busy ||
                    !data.canWrite ||
                    selected.recovery?.referral?.status === "submitted" ||
                    selected.recovery?.disputed
                  }
                  onChange={(e) =>
                    run("automation", {
                      paused: !e.target.checked,
                      disputed: false,
                    })
                  }
                />
                <span />
                {selected.recovery?.paused ||
                selected.recovery?.referral?.status === "submitted"
                  ? "Paused"
                  : "Active"}
              </label>
            </div>
            <div className="recovery-case-actions">
              <button
                type="button"
                className="recovery-secondary"
                disabled={
                  busy ||
                  !data.canWrite ||
                  selected.recovery?.referral?.status === "submitted" ||
                  selected.recovery?.disputed
                }
                onClick={() =>
                  run("automation", {
                    paused: !selected.recovery?.paused,
                    disputed: false,
                  })
                }
              >
                {selected.recovery?.paused ? "Resume" : "Pause"}
              </button>
              {selected.canRecordPayment ? (
                <button
                  type="button"
                  className="recovery-secondary"
                  disabled={busy || !data.canWrite}
                  onClick={() => go("payment")}
                >
                  Record payment
                </button>
              ) : (
                <Link
                  className="recovery-secondary"
                  to={`/invoices/payment/${selected._id}`}
                >
                  Record instalment payment
                </Link>
              )}
            </div>
            <label className="recovery-dispute">
              <input
                type="checkbox"
                checked={Boolean(selected.recovery?.disputed)}
                disabled={busy || !data.canWrite}
                onChange={(e) =>
                  run("automation", {
                    paused: true,
                    disputed: e.target.checked,
                  })
                }
              />
              Disputed invoice · pause follow-up
            </label>
          </>
        )}
        {partnerNoticeNeeded && (
          <div className="recovery-notice">
            <p>Notify the receiving partner of the current balance.</p>
            <button
              type="button"
              disabled={busy || !data.canWrite}
              onClick={() => run("partner-update", {})}
            >
              Send partner update
            </button>
          </div>
        )}
        <h3>History</h3>
        <ol className="recovery-timeline">
          {historyFor(selected).map((event, index) => (
            <li key={index}>
              <time dateTime={event.at}>{longDate(event.at)}</time>
              <strong>
                {event.title}
                {event.amount != null ? ` · ${money(event.amount)}` : ""}
              </strong>
              {event.reference && (
                <p>
                  Reference {event.reference}
                  {event.balance != null
                    ? ` · balance ${money(event.balance)}`
                    : ""}
                </p>
              )}
              {event.note && <p>{event.note}</p>}
            </li>
          ))}
        </ol>
      </div>
      <footer>{selected.invoiceNumber}</footer>
    </aside>
  );
  return (
    <div className={`recovery-shell${controller.darkMode ? " dark" : ""}`}>
      <DashboardNavigation
        open={navOpen}
        onClose={() => setNavOpen(false)}
        activePath="/debts"
        debtBadge={
          data ? data.cases.filter((row) => row.balance > 0).length : null
        }
        organisation={data?.businessName || user.companyName || "Your business"}
        role={user.roleName || "Owner"}
      />
      <div className="recovery-main">
        <AppHeader
          searchPlaceholder="Search invoices, quotes, contacts"
          onOpenNav={() => setNavOpen(true)}
          userName={[user.firstName, user.lastName].filter(Boolean).join(" ")}
        />
        <main className="recovery-page">
          {routeAction === "demand" && (
            <nav className="recovery-breadcrumb">
              <Link to="/debts">Debt collection</Link>
              <span>›</span>
              <Link to={`/debts/view/${id}`}>
                {selected?.invoiceNumber || "Case"}
              </Link>
              <span>›</span>
              <strong>Review demand</strong>
            </nav>
          )}
          <header className="recovery-heading">
            <h1>
              {routeAction === "demand"
                ? "Review letter of demand"
                : "Debt collection"}
            </h1>
            <p>
              {routeAction === "demand"
                ? "Review the details and wording before you approve sending."
                : "Manage follow-ups and recover outstanding balances."}
            </p>
          </header>
          {error && (
            <div className="recovery-error" role="alert">
              {error}
              <button
                type="button"
                disabled={busy}
                onClick={() => {
                  setError("");
                  setLoading(true);
                  load()
                    .catch((e) => setError(e.message))
                    .finally(() => setLoading(false));
                }}
              >
                <LuRefreshCw />
                Refresh case data
              </button>
            </div>
          )}
          {message && (
            <div role="status" className="recovery-success">
              <LuCheck />
              {message}
            </div>
          )}
          {loading ? (
            <RecoveryLoadingSkeleton
              detail={Boolean(id) || !selectionClosed}
              demand={routeAction === "demand"}
            />
          ) : !data ? null : id && !selected ? (
            <section className="recovery-panel">
              <h2>Recovery case unavailable</h2>
              <p>The case may no longer be available to your business.</p>
              <Link to="/debts">Back to recovery cases</Link>
            </section>
          ) : routeAction === "demand" ? (
            <div className="recovery-demand-grid">
              <div>
                <article className="recovery-panel recovery-letter">
                  <h2>Wording preview</h2>
                  <div className="recovery-letter-brand">
                    <strong>{data.businessName}</strong>
                    <span className="recovery-badge">Draft</span>
                  </div>
                  {previewLoading ? (
                    <CardSkeleton variant="summary" />
                  ) : preview ? (
                    <div className="recovery-letter-text">
                      {preview.text
                        .split("\n\n")
                        .filter(
                          (paragraph) =>
                            !paragraph.startsWith("Invoice attached ·")
                        )
                        .map((paragraph, index) => (
                          <p key={index}>
                            {index === 0 &&
                            paragraph.startsWith(`${data.businessName}\n`)
                              ? paragraph.split("\n").slice(1).join("\n")
                              : paragraph}
                          </p>
                        ))}
                    </div>
                  ) : (
                    <div className="recovery-next">
                      <strong>Choose the reviewed response date</strong>
                      <p>
                        The letter preview is generated from the current invoice
                        and balance after you select a date. No date is assumed
                        to be a legal default.
                      </p>
                    </div>
                  )}
                </article>
                <p className="recovery-attachment-note">
                  Invoice attached · No added interest, legal fees or collection
                  charges.
                </p>
              </div>
              <aside className="recovery-demand-aside">
                <section className="recovery-panel">
                  <h2>Confirm details</h2>
                  <label>
                    Recipient
                    <input
                      readOnly
                      value={selected.recipient || "Contact email not recorded"}
                    />
                  </label>
                  <p>{selected.customerName} · invoice contact</p>
                  <dl className="recovery-case-facts">
                    <dt>Invoice</dt>
                    <dd>{selected.invoiceNumber}</dd>
                    <dt>Balance ({currency})</dt>
                    <dd>{money(selected.balance)}</dd>
                  </dl>
                  <PickDate
                    id="recovery-response-date"
                    label="Reviewed response date"
                    selected={localDate(responseDate)}
                    minDate={localDate(data.today)}
                    dateFormat="d MMM yyyy"
                    disabled={busy}
                    onChange={(value) => {
                      setError("");
                      setResponseDate(inputDay(value));
                      setApproved(false);
                    }}
                  />
                  <p>Choose and review this date; it is not a legal default.</p>
                </section>
                <div className="recovery-notice">
                  <LuTriangleAlert />
                  <div>
                    <strong>Verify before sending</strong>
                    <p>
                      Verify Australian demand wording, the response date and
                      any recoverable fees. This is not a lawyer letter. Pause
                      disputed debts; check recipient and contact/privacy
                      safeguards.
                    </p>
                  </div>
                </div>
                <section className="recovery-panel">
                  <h2>Approval to send</h2>
                  <label className="recovery-check">
                    <input
                      type="checkbox"
                      checked={approved}
                      disabled={!preview || busy}
                      onChange={(e) => setApproved(e.target.checked)}
                    />
                    I reviewed the recipient, balance, response date and
                    wording, and approve sending this letter of demand.
                  </label>
                  {!data.owner && <p>Business-owner approval is required.</p>}
                  <button
                    type="button"
                    className="recovery-primary"
                    disabled={
                      !canContact ||
                      !approved ||
                      !preview ||
                      busy ||
                      previewLoading
                    }
                    onClick={() =>
                      run(
                        "demand",
                        {
                          approved: true,
                          responseDate,
                          reviewedText: preview.text,
                          recipient: preview.recipient,
                        },
                        true
                      )
                    }
                  >
                    {busy ? "Sending…" : "Approve & send demand"}
                  </button>
                  {returnButton}
                </section>
              </aside>
            </div>
          ) : (
            <>
              <div className="recovery-context">
                <p>
                  {data.businessName} · Recovery cases · {currency}
                </p>
                {currencies.length > 1 && (
                  <ThemedSelect
                    aria-label="Recovery currency"
                    dark={controller.darkMode}
                    value={currency}
                    onChange={(e) => {
                      setCurrency(e.target.value);
                      setSelectionClosed(false);
                      navigate("/debts");
                    }}
                  >
                    {currencies.map((code) => (
                      <option key={code} value={code}>
                        {code}
                      </option>
                    ))}
                  </ThemedSelect>
                )}
              </div>
              <section className="recovery-stats" aria-label="Recovery summary">
                {[
                  [
                    "Outstanding",
                    money(amountOutstanding, true),
                    `${
                      cases.filter((row) => row.balance > 0).length
                    } issued unpaid invoice${
                      cases.filter((row) => row.balance > 0).length === 1
                        ? ""
                        : "s"
                    }`,
                  ],
                  [
                    "Needs review",
                    counts.review,
                    counts.review
                      ? "Letter or referral ready for review"
                      : "No pending action",
                  ],
                  [
                    "Collected",
                    money(selected?.collectedAmount || 0, true),
                    "This recovery case",
                  ],
                ].map(([title, value, description]) => (
                  <article className="recovery-stat" key={title}>
                    <h2>
                      {title}
                      <LuCoins />
                    </h2>
                    <strong>{value}</strong>
                    <p>{description}</p>
                  </article>
                ))}
              </section>
              <nav className="recovery-tabs document-status-tabs" aria-label="Recovery case status">
                {Object.entries(titles).map(([key, label]) => (
                  <button
                    type="button"
                    key={key}
                    aria-pressed={tab === key}
                    className={tab === key ? "is-active" : ""}
                    onClick={() => setTab(key)}
                  >
                    {label} ({counts[key]})
                  </button>
                ))}
              </nav>
              <div
                className={`recovery-workspace${
                  selected ? " has-selection" : ""
                }`}
              >
                <div className="recovery-register">
                  <div className="recovery-search">
                    <LuSearch />
                    <ClearableSearchInput
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder="Search invoices..."
                      aria-label="Search recovery invoices"
                    />
                  </div>
                  <section
                    className="recovery-case-list"
                    ref={caseListRef}
                  >
                    <header>
                      <h2>
                        {titles[tab]} ({filtered.length})
                      </h2>
                      <span>{currency}</span>
                    </header>
                    <div className="recovery-list-head">
                      <strong>Customer / Invoice</strong>
                      <strong>Balance</strong>
                    </div>
                    {visible.length ? (
                      visible.map((row) => (
                        <button
                          className={`recovery-row${
                            selected?._id === row._id ? " is-selected" : ""
                          }`}
                          type="button"
                          key={row._id}
                          onClick={() => select(row)}
                        >
                          <span>
                            <strong>
                              {row.customerName || "Contact unavailable"}
                            </strong>
                            <span className="recovery-reference">
                              {row.invoiceNumber}
                            </span>
                          </span>
                          <span
                            className={`recovery-row-balance ${
                              row.balance > 0 ? "is-owing" : ""
                            }`}
                          >
                            {money(row.balance)}
                          </span>
                          <span className="recovery-row-context">
                            <span className={`recovery-badge is-${row.group}`}>
                              {row.status}
                            </span>
                            <span>Due {date(row.dueDate)}</span>
                          </span>
                          <small>{row.nextAction}</small>
                        </button>
                      ))
                    ) : (
                      <p className="recovery-empty">
                        {search
                          ? "No cases match your search."
                          : `No ${titles[tab].toLowerCase()} cases.`}
                      </p>
                    )}
                    <footer>
                      <span>
                        Showing{" "}
                        {filtered.length
                          ? (currentPage - 1) * RECOVERY_PAGE_SIZE + 1
                          : 0}
                        –
                        {Math.min(
                          currentPage * RECOVERY_PAGE_SIZE,
                          filtered.length
                        )}{" "}
                        of{" "}
                        {filtered.length} case{filtered.length === 1 ? "" : "s"}
                      </span>
                      {filtered.length > RECOVERY_PAGE_SIZE && (
                        <div className="recovery-pagination">
                          <button
                            aria-label="Previous cases"
                            disabled={currentPage <= 1}
                            onClick={() => setPage((p) => p - 1)}
                          >
                            <LuChevronLeft />
                          </button>
                          <span aria-live="polite">
                            Page {currentPage} of {pageCount}
                          </span>
                          <button
                            aria-label="Next cases"
                            disabled={currentPage >= pageCount}
                            onClick={() => setPage((p) => p + 1)}
                          >
                            <LuChevronRight />
                          </button>
                        </div>
                      )}
                    </footer>
                  </section>
                  {responsible}
                </div>
                {caseCard}
              </div>
            </>
          )}
        </main>
      </div>
      {["payment", "referral"].includes(routeAction) && selected && (
        <div className="recovery-modal-backdrop">
          <section
            className="recovery-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="recovery-modal-title"
            ref={dialogRef}
          >
            <header>
              <h2 id="recovery-modal-title">
                {routeAction === "payment"
                  ? "Record payment"
                  : "Confirm collection referral"}
              </h2>
              <button
                type="button"
                aria-label="Close dialog"
                disabled={busy}
                onClick={closeAction}
              >
                <LuX />
              </button>
            </header>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (routeAction === "payment") run("payments", payment, true);
                else
                  run(
                    "referral",
                    {
                      approved,
                      partnerName: partner.name,
                      partnerEmail: partner.email,
                    },
                    true
                  );
              }}
            >
              <div className="recovery-modal-body">
                <p>
                  {selected.customerName} · {selected.invoiceNumber}
                </p>
                {routeAction === "payment" ? (
                  <>
                    <dl className="recovery-case-facts">
                      <dt>Balance before payment</dt>
                      <dd>{money(selected.balance)}</dd>
                    </dl>
                    <label>
                      Payment amount ({currency})
                      <input
                        type="number"
                        min="0.01"
                        step="0.01"
                        max={selected.balance}
                        required
                        value={payment.amount}
                        onChange={(e) =>
                          setPayment({ ...payment, amount: e.target.value })
                        }
                      />
                    </label>
                    <PickDate
                      id="recovery-payment-date"
                      label="Payment date"
                      selected={localDate(payment.paymentDate)}
                      maxDate={localDate(data.today)}
                      dateFormat="d MMM yyyy"
                      disabled={busy}
                      onChange={(value) =>
                        setPayment({ ...payment, paymentDate: inputDay(value) })
                      }
                    />
                    <label>
                      Payment reference
                      <input
                        required
                        maxLength={80}
                        value={payment.reference}
                        onChange={(e) =>
                          setPayment({ ...payment, reference: e.target.value })
                        }
                      />
                    </label>
                    <div className="recovery-next">
                      <strong>
                        Remaining balance{" "}
                        {money(
                          Math.max(
                            0,
                            (Math.round(selected.balance * 100) -
                              Math.round(Number(payment.amount || 0) * 100)) /
                              100
                          )
                        )}
                      </strong>
                      <p>
                        {Number(payment.amount) < selected.balance
                          ? "Partial payment · case stays open."
                          : "The case closes only when the full outstanding amount is received."}
                        {selected.recovery?.referral?.status === "submitted"
                          ? " Referred case reminders remain stopped."
                          : ""}
                      </p>
                    </div>
                    <p>
                      Recording a payment does not charge the customer. Confirm
                      receipt before saving;{" "}
                      {selected.recovery?.referral?.status === "submitted"
                        ? "the receiving partner will be emailed the updated balance."
                        : "no funds are transferred."}
                    </p>
                  </>
                ) : (
                  <>
                    <dl className="recovery-case-facts">
                      <dt>Outstanding ({currency})</dt>
                      <dd>{money(selected.balance)}</dd>
                      <dt>Demand sent</dt>
                      <dd>{date(selected.recovery?.demand?.sentAt)}</dd>
                      <dt>Response date</dt>
                      <dd>{date(selected.recovery?.demand?.responseDate)}</dd>
                    </dl>
                    <p>
                      Review any recorded payment or dispute.
                      Confirm the current balance before referring.
                    </p>
                    <div className="recovery-next">
                      <strong>Share necessary information only</strong>
                      <p>
                        Invoice and balance, business contact, approved demand
                        and relevant reminder/payment history. Exclude unrelated
                        personal information.
                      </p>
                    </div>
                    <label>
                      Verified receiving partner
                      <input
                        required
                        maxLength={160}
                        value={partner.name}
                        onChange={(e) =>
                          setPartner({ ...partner, name: e.target.value })
                        }
                      />
                    </label>
                    <label>
                      Verified partner email
                      <input
                        type="email"
                        required
                        maxLength={254}
                        value={partner.email}
                        onChange={(e) =>
                          setPartner({ ...partner, email: e.target.value })
                        }
                      />
                    </label>
                    <div className="recovery-notice">
                      <LuTriangleAlert />
                      <div>
                        <strong>Verify partner requirements</strong>
                        <p>
                          Confirm eligibility, terms and charges before
                          submission. No unverified fees are added. Pause
                          disputed debts; check privacy and lawful contact
                          requirements.
                        </p>
                      </div>
                    </div>
                    <label className="recovery-check">
                      <input
                        type="checkbox"
                        required
                        checked={approved}
                        onChange={(e) => setApproved(e.target.checked)}
                      />
                      I have verified the partner and terms, and explicitly
                      approve sharing the necessary information for this
                      referral.
                    </label>
                    <p>
                      After submission: reminders stop. Next action is partner
                      follow-up; record any payments and notify the partner.
                      Email submission does not confirm partner acceptance.
                    </p>
                  </>
                )}
                {error && (
                  <p role="alert" className="recovery-error">
                    {error}
                  </p>
                )}
              </div>
              <footer>
                <button
                  className="recovery-secondary"
                  type="button"
                  disabled={busy}
                  onClick={closeAction}
                >
                  Cancel
                </button>
                <button
                  className="recovery-primary"
                  disabled={
                    busy ||
                    !data.canWrite ||
                    (routeAction === "payment" && !selected.canRecordPayment) ||
                    (routeAction === "referral" &&
                      (!data.owner ||
                        !approved ||
                        !referralReady ||
                        !canContact))
                  }
                >
                  {busy
                    ? "Saving…"
                    : routeAction === "payment"
                    ? "Save payment"
                    : "Approve referral"}
                </button>
              </footer>
            </form>
          </section>
        </div>
      )}
    </div>
  );
}
